// packages/core/src/learning/school-modules/risk-management.ts
// ─────────────────────────────────────────────────────────────────────────
// Lens Pack for the 'risk-management' school. LearningModule, surface 'school'.
//   module id   = 'school-risk-management'  (matches exhibit nativeModuleId)
//   homeSchool  = 'risk-management'
// PROMOTED: three authored lessons (above the divider in the index).
//
// Placeholder presentational fields (emoji, accentColor, tier, order) carried
// from the stub conventions — tune per-school in the presentation pass.
// ─────────────────────────────────────────────────────────────────────────

import type { LearningModule } from '../LearningTypes';

export const SCHOOL_RISK_MANAGEMENT_MODULE: LearningModule = {
	id: 'school-risk-management',
	title: 'Risk Management',
	description:
		'The lens that asks what can break before chasing what can grow — runway, protection, and the cost of too much caution.',
	emoji: '🛡️',
	accentColor: '#3f7d33',
	tier: 'stability',
	pillar: 'stability-protection',
	order: 0,
	surface: 'school',
	homeSchool: 'risk-management',
	lessons: [
		{
			id: 'school-risk-management-01',
			title: 'The downside gets a vote',
			description:
				'Learn why risk management starts by asking what can break before chasing what can grow.',
			pillar: 'stability-protection',
			schoolsOfThought: ['risk-management'],
			estimatedReadMinutes: 4,
			estimatedListenMinutes: 5,
			difficulty: 2,
			tags: ['risk-management', 'fragility', 'runway'],
			prerequisites: [],
			relatedLessons: [],
			lessonKind: 'school-core',
			concepts: [
				'downside first thinking',
				'fragile plans',
				'survival before upside',
			],
			blocks: [
				{
					type: 'paragraph',
					text: 'Risk management starts with a blunt question: what happens if this goes wrong? Not because life is hopeless. Because life is real. Cars break. Jobs change. Rent rises. People get sick. Markets drop. Bills arrive at the worst time.',
				},
				{
					type: 'paragraph',
					text: 'This school believes the downside deserves a vote before you commit your money, time, or future options. It is not trying to scare you. It is trying to keep one bad event from becoming the event that controls your life.',
				},
				{
					type: 'bullet_list',
					items: [
						'What could break?',
						'How much would it cost?',
						'How long could you stay calm?',
						'What backup would reduce the damage?',
					],
				},
				{
					type: 'callout',
					text: 'The common mistake is judging a plan only by how good it looks when everything goes right.',
				},
				{
					type: 'example',
					text: 'Someone moves out with exactly enough income to cover rent, food, car insurance, and subscriptions. On paper, it works. Then a $900 car repair hits. Risk management notices that the plan was not wrong because moving out is bad. It was fragile because there was no room for normal life.',
				},
				{
					type: 'paragraph',
					text: 'The tradeoff is that downside thinking can feel slower. You may delay a move, keep more cash, choose a less exciting path, or pass on something that looks attractive. That can be frustrating when you want progress now.',
				},
				{
					type: 'paragraph',
					text: 'Inside Nisk, this lens connects naturally to checking your runway. The question is not, “Am I scared?” The question is, “How much time and choice do I have if the next month is worse than expected?”',
				},
				{
					type: 'key_takeaway',
					text: 'A plan is not strong because it looks good. It is strong because it can take a hit.',
				},
			],
			audioScript:
				'Risk management begins with the part most people want to skip. What happens if this does not go the way I hoped? That question can feel negative at first. You might hear it as doubt, fear, or overthinking. But in this lens, that question is not there to kill your ambition. It is there to protect your future self from getting trapped by a normal bad event.\n\nThink about a simple move out decision. You have rent, utilities, groceries, car insurance, gas, phone bill, and maybe a few subscriptions. You add it up and say, “Technically, I can afford it.” That might be true. But risk management asks the next question. What if your car needs repairs in month two? What if your hours get cut? What if the deposit costs more than expected? What if the furniture, kitchen stuff, parking, or internet setup costs more than you planned? The plan might still be possible, but now you can see whether it is stable or fragile.\n\nFragile does not mean dumb. A fragile plan is simply a plan where one ordinary problem creates a much bigger problem. A $700 repair becomes credit card debt. A missed paycheck becomes rent panic. A medical bill becomes a month of stress. The event itself is not always huge. The damage comes from having no buffer.\n\nRisk management believes the first job of money is not to make you look successful. It is to keep you from getting wiped out. That does not mean you never take chances. It means you know what could go wrong before you step forward. There is a big difference between courage and walking blind.\n\nThe tradeoff is real. If you give the downside a vote, you might move slower. You might keep more cash than someone on social media says is optimal. You might wait another month before a big decision. You might choose the used car, the smaller apartment, the simpler plan, or the backup option. That can feel boring. It can even feel like you are behind. But boring can buy something valuable: calm.\n\nInside Nisk, this is why runway matters. Runway is not just a number. It is how long you can keep making clear decisions if life gets messy. When your runway is thin, every problem feels personal and urgent. When your runway is stronger, you can think, compare, pause, and choose.\n\nThe point is not to live scared. The point is to stop pretending everything will go perfectly. Risk management asks you to respect reality before reality sends the bill.',
			flashcards: [
				{
					id: 'school-risk-management-01-fc-1',
					role: 'main_idea',
					front: 'What does risk management notice first?',
					back: 'It notices what can break before chasing upside.',
				},
				{
					id: 'school-risk-management-01-fc-2',
					role: 'watch_out',
					front: 'What makes a plan fragile?',
					back: 'One normal bad event can create major damage.',
				},
				{
					id: 'school-risk-management-01-fc-3',
					role: 'use_it',
					front: 'How do you test this lens?',
					back: 'Ask what could go wrong and how much runway you have.',
				},
			],
			applyAction: {
				type: 'calculateRunway',
				label: 'Check your runway',
			},
			revision: 1,
		},
		{
			id: 'school-risk-management-02',
			title: 'Protection is not wasted money',
			description:
				'Learn why risk management treats reserves, insurance, and backup plans as useful protection, not dead weight.',
			pillar: 'stability-protection',
			schoolsOfThought: ['risk-management'],
			estimatedReadMinutes: 4,
			estimatedListenMinutes: 5,
			difficulty: 2,
			tags: ['risk-management', 'insurance', 'emergency-fund'],
			prerequisites: [],
			relatedLessons: [],
			lessonKind: 'school-core',
			concepts: [
				'protection as capacity',
				'backup systems',
				'calm under stress',
			],
			blocks: [
				{
					type: 'paragraph',
					text: 'Protection usually feels unnecessary right up until the moment it matters. Emergency cash feels idle. Insurance feels annoying. Backup documents feel boring. Then life hits, and the boring thing becomes the thing that keeps you standing.',
				},
				{
					type: 'paragraph',
					text: 'Risk management believes protection is not wasted money just because you do not use it every week. Some money exists to create capacity. It gives you time, options, and calm when a problem shows up.',
				},
				{
					type: 'bullet_list',
					items: [
						'Cash can buy time',
						'Insurance can limit damage',
						'Documents can reduce confusion',
						'Clear records can prevent panic',
					],
				},
				{
					type: 'callout',
					text: 'The common mistake is thinking unused protection was pointless.',
				},
				{
					type: 'example',
					text: 'Imagine two people with the same income and the same surprise medical bill. One has no cash buffer and no clear plan. The other has one month of expenses saved and knows which bills can wait. The bill is still annoying for both. But the second person has more room to respond.',
				},
				{
					type: 'paragraph',
					text: 'The tradeoff is that protection can feel inefficient. Money held for emergencies might not grow much. Insurance premiums can feel like money leaving forever. Time spent organizing documents can feel like time you would rather use somewhere else.',
				},
				{
					type: 'paragraph',
					text: 'Inside Nisk, this lens asks you to look at your current money picture honestly. Your goal is not to create a perfect fortress. It is to notice which missing protection would hurt most if life got weird next month.',
				},
				{
					type: 'key_takeaway',
					text: 'Protection is valuable because it gives you choices when stress tries to take them away.',
				},
			],
			audioScript:
				'Protection is one of the hardest money ideas to respect because it often looks useless when life is calm. You pay for insurance and nothing happens. You keep cash in reserve and it just sits there. You organize documents and nobody applauds. You write down account details, renewal dates, and emergency contacts, and it does not feel like progress. It feels like maintenance.\n\nRisk management sees it differently. This lens believes some money is supposed to protect your ability to make decisions. Not every dollar has to chase growth. Not every system has to be exciting. Some systems exist so that one bad week does not turn into a six month mess.\n\nThink about emergency savings. A cash buffer is not impressive on a chart compared with an investment projection. But if your car breaks, your hours get cut, or a family emergency pulls you away from work, cash has a job. It buys time. It prevents panic borrowing. It lets you compare choices instead of grabbing the first expensive option because the deadline is today.\n\nInsurance works in a similar way. It can feel frustrating because you are paying for something you hope not to use. But risk management does not ask, “Did this feel exciting?” It asks, “What damage would this stop?” The point is not to insure every tiny inconvenience. The point is to protect against events that could seriously damage your life, your income, your housing, your health, or your family responsibilities.\n\nDocuments are another boring layer. Lease agreements, car titles, health records, tax forms, passwords, policy numbers, and emergency contacts do not feel like money until something goes wrong. Then access matters. A missing document can create delays. A forgotten renewal can create fees. A unclear agreement can create conflict.\n\nThe tradeoff is that protection can feel inefficient. If you keep more cash, you may feel like you are missing growth. If you pay for coverage, you may feel like you are losing money every month. If you slow down to read a policy or organize records, you may feel like you are being too cautious. This is where the lens needs balance. Too little protection creates fragility. Too much protection can make life feel frozen.\n\nThe goal is not to build a perfect bunker. The goal is to know which risks would hurt most and which simple protections reduce the damage. Inside Nisk, that starts with looking at your current money picture. How much room do you have? What would happen after one missed paycheck? What bill would create the most stress? What document would you need fast?\n\nProtection is not glamorous. But when stress arrives, protection becomes options. And options are the whole point.',
			flashcards: [
				{
					id: 'school-risk-management-02-fc-1',
					role: 'main_idea',
					front: 'Why does protection matter?',
					back: 'It gives you time and choices during stress.',
				},
				{
					id: 'school-risk-management-02-fc-2',
					role: 'watch_out',
					front: 'What is the protection trap?',
					back: 'Thinking protection is useless because you have not needed it yet.',
				},
				{
					id: 'school-risk-management-02-fc-3',
					role: 'use_it',
					front: 'What should you check first?',
					back: 'Check which missing protection would hurt most next month.',
				},
			],
			applyAction: {
				type: 'openDashboard',
				label: 'Review your money picture',
			},
			revision: 1,
		},
		{
			id: 'school-risk-management-03',
			title: 'Too much caution has a cost',
			description:
				'Learn where risk management breaks down when protection turns into fear, delay, and missed growth.',
			pillar: 'stability-protection',
			schoolsOfThought: ['risk-management'],
			estimatedReadMinutes: 5,
			estimatedListenMinutes: 6,
			difficulty: 3,
			tags: ['risk-management', 'caution-tradeoff', 'resilient-growth'],
			prerequisites: [],
			relatedLessons: [],
			lessonKind: 'school-core',
			concepts: [
				'caution tradeoff',
				'risk avoidance trap',
				'resilient growth',
			],
			blocks: [
				{
					type: 'paragraph',
					text: 'Risk management can save you from fragile decisions, but it can also become a hiding place. If every move feels too risky, you may protect yourself from danger and also protect yourself from growth.',
				},
				{
					type: 'paragraph',
					text: 'This school believes downside matters. But a mature version of the lens also admits that avoiding every risk is impossible. Life itself has risk. Staying still has risk. Waiting forever has risk too.',
				},
				{
					type: 'bullet_list',
					items: [
						'Keeping cash can help',
						'Keeping only cash can fall behind',
						'Avoiding debt can reduce stress',
						'Avoiding every investment can limit growth',
						'Waiting can create clarity',
						'Waiting forever can close doors',
					],
				},
				{
					type: 'callout',
					text: 'The common mistake is confusing safety with never making a move.',
				},
				{
					type: 'example',
					text: 'Someone wants to build skills for a better career but refuses every course, conference, move, or job change because each one has uncertainty. The fear is understandable. But the result may be a different risk: staying stuck in low income, low confidence, and low options.',
				},
				{
					type: 'paragraph',
					text: 'The tradeoff is that risk management must decide which risks are worth reducing and which risks are worth taking carefully. A plan with no downside protection is fragile. A life with no chosen risk can become small.',
				},
				{
					type: 'paragraph',
					text: 'Inside Nisk, this lens connects to simulation. You can test a choice instead of arguing with fear in your head. Seeing the tradeoff in numbers can help you separate real danger from vague anxiety.',
				},
				{
					type: 'key_takeaway',
					text: 'Risk management works best when it protects your future without shrinking your life.',
				},
			],
			audioScript:
				'Risk management is powerful, but it has a shadow side. Once you get good at seeing what can go wrong, you can start seeing danger everywhere. The car could break. The job could fail. The market could drop. The move could be expensive. The relationship could change. The business could flop. The plan could be wrong. All of that may be true. But if the only lesson you take from risk management is “do nothing,” the lens has gone too far.\n\nA mature risk management lens does not say, “Avoid all risk.” That is impossible. It says, “Understand the risk. Reduce the risks that can wipe you out. Take chosen risks with enough room to recover.” That is a very different mindset.\n\nFor example, keeping cash can be smart. It gives you runway. But keeping every dollar in cash forever has its own cost. Prices rise. Opportunities pass. Your future self may need growth, skills, and ownership, not just a bigger emergency cushion. Avoiding debt can reduce stress. But refusing every kind of investment in yourself because there is uncertainty can keep you stuck. Waiting can create clarity. But waiting forever can become a quiet form of fear.\n\nThis is where risk management has to be honest. There is a difference between caution and avoidance. Caution asks better questions. Avoidance refuses to choose. Caution says, “What would make this decision safer?” Avoidance says, “What if something goes wrong, so I will not move.” Caution creates a plan. Avoidance creates delay.\n\nThink about career growth. Maybe you want a better role, a certification, a move, or a side project. There is risk. You might spend time and not get the result. You might feel awkward. You might lose money. But there is also risk in staying where you are if your income is low, your skills are not growing, or your current job is fragile. Risk management has to count both sides. The risk of action and the risk of inaction.\n\nInside Nisk, this is where simulation helps. Instead of letting fear stay vague, you can test a decision against your real numbers. What happens if the opportunity costs $500? What happens if income rises later? What happens if it fails and you need three months to recover? What runway would make the risk feel more responsible? Numbers will not make the decision for you, and they should not pretend to predict the future. But they can turn fog into a clearer tradeoff.\n\nThe goal is not to become fearless. Fear has information. The goal is to avoid being ruled by fear. Risk management should protect your future, not shrink your life until nothing meaningful is left to protect.',
			flashcards: [
				{
					id: 'school-risk-management-03-fc-1',
					role: 'main_idea',
					front: 'Where can risk management go wrong?',
					back: 'It can turn into fear, delay, and avoidance.',
				},
				{
					id: 'school-risk-management-03-fc-2',
					role: 'watch_out',
					front: 'What is the avoidance trap?',
					back: 'Thinking safety means never making a move.',
				},
				{
					id: 'school-risk-management-03-fc-3',
					role: 'use_it',
					front: 'How can you balance caution?',
					back: 'Test the risk, protect the downside, then compare the tradeoff.',
				},
			],
			applyAction: {
				type: 'openSimulator',
				label: 'Simulate a careful risk',
			},
			revision: 1,
		},
	],
};
